import { useCallback, useMemo, useEffect } from 'react';
import { useFetcher } from '@remix-run/react';
import type { ResendInviteMutation } from 'generated/graphql';
import { RESEND_BUILD_HUB_COLLABORATOR_API_ROUTE } from '~/routes/remix-api.app.build-hub-collaborator-resend';

interface UseBuildHubCollaboratorResendInviteOptions {
    onSuccess?: () => void;
}

interface UseBuildHubCollaboratorResendInviteReturn {
    resendInvite: (formData: FormData) => void;
    isUpdating: boolean;
    error: string | null;
    isSuccess: boolean;
    isIdle: boolean;
}

/**
 * Hook to handle invite resending using Remix fetcher
 */
export function useBuildHubCollaboratorResend(
    options: UseBuildHubCollaboratorResendInviteOptions = {}
): UseBuildHubCollaboratorResendInviteReturn {
    const { onSuccess } = options;
    const resendFetcher = useFetcher<ResendInviteMutation>();
    const data = resendFetcher.data?.buildHubInviteResend;
    const isUpdating = resendFetcher.state !== 'idle';
    const isIdle = !isUpdating;
    const isSuccess = Boolean(
        isIdle && data && data.__typename === 'BuildHubCollaborator'
    );

    const resendInvite = useCallback(
        (formData: FormData) => {
            resendFetcher.submit(formData, {
                method: 'post',
                action: RESEND_BUILD_HUB_COLLABORATOR_API_ROUTE,
            });
        },
        [resendFetcher]
    );

    const error = useMemo(() => {
        if (isIdle && data && data.__typename !== 'BuildHubCollaborator') {
            return 'Message' in data ? data.Message : 'Failed to send invite';
        }
        return null;
    }, [isIdle, data]);

    useEffect(() => {
        if (isSuccess) {
            onSuccess?.();
        }
    }, [isSuccess, onSuccess]);

    return {
        resendInvite,
        isUpdating,
        error,
        isSuccess,
        isIdle,
    };
}
